import { MenuItem, SelectProps } from '@mui/material';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { find, findIndex, isObject } from 'lodash';
import { ReactElement, ReactNode, useState } from 'react';
import { Controller } from 'react-hook-form';

import { FormElementProps } from '@/components/form/types';

interface FormSelectProps extends FormElementProps {
    inputFieldProps?: TextFieldProps;
    options?: ({ value: string; label: ReactNode; menuItem?: ReactNode } | { decoration: ReactElement })[];
    effectOnChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const FormSelect = ({
    name,
    control,
    label,
    options = [],
    inputFieldProps: { slotProps, ...inputFieldProps } = {},
    controllerProps = {},
    effectOnChange = () => {},
}: FormSelectProps) => {
    const valueOptions = options.filter((o) => 'value' in o) as { value: string; label: string }[];
    const [open, setOpen] = useState(false);
    const selectSlotProps = slotProps?.select as SelectProps;

    return (
        <Controller
            name={name}
            control={control}
            {...controllerProps}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
                const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(event.target.value);
                    if (typeof effectOnChange === 'function') {
                        effectOnChange(event);
                    }
                };
                const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'ArrowDown') {
                        onChange(valueOptions[(findIndex(valueOptions, { value }) + 1) % valueOptions.length].value);
                    }
                    if (event.key === 'ArrowUp') {
                        onChange(
                            valueOptions[
                                (findIndex(valueOptions, { value }) - 1 + valueOptions.length) % valueOptions.length
                            ].value
                        );
                    }
                };
                return (
                    <TextField
                        slotProps={{
                            ...slotProps,
                            select: {
                                ...selectSlotProps,
                                open,
                                onOpen: (e) => {
                                    if (
                                        e.type === 'keydown' &&
                                        'key' in e &&
                                        (e.key === 'ArrowDown' || e.key === 'ArrowUp')
                                    ) {
                                        // ignore open requests from up/down arrows; just let our own keypress handler
                                        // take care of it
                                        return;
                                    }
                                    setOpen(true);
                                    selectSlotProps?.onOpen?.(e);
                                },
                                onClose: (e) => {
                                    setOpen(false);
                                    selectSlotProps?.onClose?.(e);
                                },
                                renderValue: (value) => {
                                    if (selectSlotProps?.renderValue) {
                                        return selectSlotProps.renderValue(value);
                                    }
                                    const option = find(options, { value });
                                    if (isObject(option) && 'label' in option) {
                                        return option.label;
                                    }
                                    return String(value);
                                },
                            },
                        }}
                        onKeyDown={handleKeyDown}
                        select
                        helperText={error ? error.message : null}
                        size="small"
                        error={!!error}
                        onChange={handleOnChange}
                        value={value}
                        fullWidth
                        label={label}
                        variant="outlined"
                        {...inputFieldProps}
                    >
                        {options.map((option, idx) =>
                            'decoration' in option ? (
                                <span key={idx}>{option.decoration}</span>
                            ) : (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.menuItem ?? option.label}
                                </MenuItem>
                            )
                        )}
                    </TextField>
                );
            }}
        />
    );
};
